import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  ERROR_MEMBER_RETRIEVAL,
  ERROR_TITLE_GENERIC,
} from "@/constants/errors";
import { UserCog, Users } from "lucide-react";
import {
  selectConversations,
  selectCurrConversation,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import DeleteButton from "./DeleteButton";
import { GroupMember } from "@/types/users";
import { Search } from "lucide-react";
import { Toggle } from "../ui/toggle";
import { getGroupMembers } from "@/services/conversationService";
import { processFullname } from "@/utils/utils";
import { toggleIsSearchView } from "@/features/details/detailsSlice";
import { useToast } from "../ui/use-toast";

const DetailsView = () => {
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const [name, setName] = useState<string>("");
  const [nameDetails, setNameDetails] = useState<string>("");
  const [showMembers, setShowMembers] = useState<boolean>(false);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [memberError, setMemberError] = useState<boolean>(false);
  const { toast } = useToast();

  const handleToggleMembers = () => {
    setShowMembers(!showMembers);

    if (memberError && showMembers) {
      setMemberError(false);
    }
  };

  const renderGroupMembers = useCallback(() => {
    if (memberError) {
      return null;
    }

    if (members.length === 0) {
      return (
        <p className="text-sm text-neutral-400 text-center py-2">No members found</p>
      );
    }

    return members.map((m: GroupMember) => {
      const username = m.username;
      let name = "";

      if (m.firstName && m.lastName) {
        name = ` (${m.firstName} ${m.lastName})`;
      } else if (m.firstName || m.lastName) {
        name = ` (${m.firstName || m.lastName})`;
      }

      return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-neutral-100" key={username}>
          {m.isOwner && <UserCog size={16} className="shrink-0 text-neutral-500" />}
          {!m.isOwner && <div className="w-4 shrink-0" />}
          <p className="text-sm font-light">
            <span className="font-semibold">{username}</span>
            {name}
          </p>
        </div>
      );
    });
  }, [members, memberError]);

  useEffect(() => {
    if (memberError) {
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: ERROR_MEMBER_RETRIEVAL,
      });
    }
  }, [memberError, toast]);

  useEffect(() => {
    setMembers([]);
    setShowMembers(false);

    if (currConversationIndex !== null) {
      const updateInfo = async () => {
        const conversation = conversations[currConversationIndex];
        const [name, rawNameDetails] = processFullname(
          conversation.fullname,
          conversation.groupName
        );

        setName(name);
        setNameDetails(rawNameDetails.trim().replace(/^\(|\)$/g, ""));

        if (rawNameDetails.includes("Group")) {
          const gid = Number(JSON.parse(
            conversations[currConversationIndex].address
          ).gid);
          try {
            const members = await getGroupMembers(gid);
            setMembers(members);
          } catch {
            setMemberError(true);
          }
        } else {
          setShowMembers(false);
          setMembers([]);
          setMemberError(false);
        }
      };

      updateInfo();
    }
  }, [conversations, currConversationIndex]);

  return (
    <div className="flex flex-col items-center h-full p-4 pt-20">
      <div className="relative flex flex-row">
        <Avatar className="relative z-10 w-16 h-16">
          <AvatarFallback className="text-2xl font-light bg-blue-100">
            {name[0] && name[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
        {nameDetails === "Group" && (
          <Avatar className="relative w-16 h-16 -mt-4 -ml-10">
            <AvatarFallback className="text-2xl font-light bg-slate-400 bg-opacity-80"></AvatarFallback>
          </Avatar>
        )}
      </div>
      
      <p className="pt-4 text-xl font-semibold">{name}</p>
      <p className="text-lg font-light">{nameDetails}</p>
      
      <div className="flex flex-row gap-3 pt-6 pb-4">
        <div className="flex flex-col items-center gap-1">
          <Button
            variant="secondary"
            className="p-2 rounded-full h-9 hover:bg-gray-200"
            onClick={() => dispatch(toggleIsSearchView())}
          >
            <Search size={20} />
          </Button>
          <p className="text-xs text-neutral-500">Search</p>
        </div>

        {nameDetails === "Group" && (
          <div className="flex flex-col items-center gap-1">
            <Toggle
              size="sm"
              className="z-10 px-2 rounded-full"
              pressed={showMembers}
              variant="secondary"
              onPressedChange={handleToggleMembers}
            >
              <Users size={20} />
            </Toggle>
            <p className="text-xs text-neutral-500">Members</p>
          </div>
        )}

        <div className="flex flex-col items-center gap-1">
          <DeleteButton />
          <p className="text-xs text-neutral-500">Delete</p>
        </div>
      </div>

      {showMembers && (
        <div className="w-full flex flex-col gap-0.5 overflow-y-auto border rounded-md p-1">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide px-3 pt-1 pb-0.5">
            Members
          </p>
          {renderGroupMembers()}
        </div>
      )}
    </div>
  );
};

export default DetailsView;
